/**
 * TaskTicketPicker - optional ticket link field in the task
 * create / edit modal.
 *
 * Follows the searchable-picker pattern (frontend/CLAUDE.md section
 * 9, canonical example TicketOwnerPicker): a disclosure button + an
 * absolutely-positioned panel with a search input and a result list,
 * because a native <select> cannot host an input. Outside-click and
 * Escape close the panel; ArrowUp / ArrowDown / Enter walk and pick.
 *
 * Search is server-side via useAgentTicketSearch (the same endpoint
 * behind the inbox search). That hook is disabled on an empty query,
 * so the empty panel shows a "type to search" hint instead of a
 * browse list - a task link is always to a ticket the agent already
 * has in mind, so browse adds nothing here.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";

import { useAgentTicketSearch } from "@/hooks/useAgentTicketSearch";
import type { SearchResultResponse } from "@/types/api";

export interface TaskTicketValue {
  /** The public identifier the backend's ticket_ref resolver speaks
   * (e.g. "GW7", "F-104944"). */
  public_code: string;
  subject: string;
}

export interface TaskTicketPickerProps {
  value: TaskTicketValue | null;
  onChange: (next: TaskTicketValue | null) => void;
  label?: string;
  id?: string;
}

export function TaskTicketPicker({
  value,
  onChange,
  label,
  id,
}: TaskTicketPickerProps): JSX.Element {
  const autoId = useId();
  const inputId = id ?? autoId;
  const listId = `${inputId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(-1);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // The search hook is keyed on the raw query; it debounces nothing
  // itself but is disabled while the query is blank, and its 30s
  // staleTime absorbs re-opens. Results are cursor-paginated; the
  // first page (25) is plenty for a link picker.
  const search = useAgentTicketSearch(open ? query : "");
  const rows = useMemo<SearchResultResponse[]>(
    () => search.data?.pages.flatMap((p) => p.data) ?? [],
    [search.data],
  );

  useEffect(() => {
    setActiveIdx(rows.length > 0 ? 0 : -1);
  }, [rows]);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => inputRef.current?.focus());
    } else {
      setQuery("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(event: MouseEvent) {
      const target = event.target as Node | null;
      if (panelRef.current?.contains(target ?? null)) return;
      if (buttonRef.current?.contains(target ?? null)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDocClick);
    // Capture phase so Escape closes the panel BEFORE the enclosing
    // Modal's own Escape handler closes the whole dialog.
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  function pick(row: SearchResultResponse) {
    onChange({ public_code: row.public_code, subject: row.subject });
    setOpen(false);
    buttonRef.current?.focus();
  }

  function onInputKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIdx((idx) => (rows.length === 0 ? -1 : (idx + 1) % rows.length));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIdx((idx) =>
        rows.length === 0 ? -1 : (idx - 1 + rows.length) % rows.length,
      );
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = rows[activeIdx];
      if (row) pick(row);
    }
  }

  return (
    <div className="relative">
      {label && (
        <label
          htmlFor={inputId}
          className="block text-sm font-medium text-neutral-700"
        >
          {label}
        </label>
      )}
      <div className="mt-1 flex items-center gap-2">
        <button
          ref={buttonRef}
          id={inputId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => setOpen((v) => !v)}
          className="flex min-w-0 flex-1 items-center justify-between rounded-md border border-neutral-300 bg-white px-3 py-2 text-left text-sm shadow-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          data-testid="task-ticket-picker-trigger"
        >
          <span className="truncate" title={value?.subject}>
            {value ? (
              <>
                <span className="font-mono text-xs font-medium">
                  {value.public_code}
                </span>
                <span className="ml-2 text-neutral-600">{value.subject}</span>
              </>
            ) : (
              <span className="text-neutral-400">No linked ticket</span>
            )}
          </span>
          <i
            className="fa-duotone fa-thin fa-chevron-down ml-2 text-neutral-400"
            aria-hidden="true"
          />
        </button>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="rounded-md p-1 text-neutral-400 transition-colors hover:text-neutral-600"
            aria-label="Clear linked ticket"
            data-testid="task-ticket-picker-clear"
          >
            <i className="fa-duotone fa-thin fa-xmark text-base" aria-hidden="true" />
          </button>
        )}
      </div>

      {open && (
        <div
          ref={panelRef}
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-neutral-200 bg-white shadow-lg"
          data-testid="task-ticket-picker-panel"
        >
          <div className="border-b border-neutral-100 p-2">
            <input
              ref={inputRef}
              type="search"
              placeholder="Search tickets by code or subject..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onInputKeyDown}
              aria-controls={listId}
              aria-autocomplete="list"
              className="block w-full rounded border border-neutral-300 px-2 py-1 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              data-testid="task-ticket-picker-input"
            />
          </div>
          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto py-1">
            {query.trim().length === 0 && (
              <li className="px-3 py-2 text-xs text-neutral-500">
                Type to search tickets.
              </li>
            )}
            {query.trim().length > 0 && search.isLoading && (
              <li className="px-3 py-2 text-xs text-neutral-500">
                Searching...
              </li>
            )}
            {query.trim().length > 0 &&
              !search.isLoading &&
              rows.length === 0 && (
                <li className="px-3 py-2 text-xs text-neutral-500">
                  No matching tickets.
                </li>
              )}
            {rows.map((row, idx) => (
              <li
                key={row.ticket_id}
                role="option"
                aria-selected={idx === activeIdx}
                onMouseDown={(e) => {
                  // mousedown so the outside-click handler doesn't
                  // close the panel before the click fires.
                  e.preventDefault();
                  pick(row);
                }}
                onMouseEnter={() => setActiveIdx(idx)}
                className={`cursor-pointer px-3 py-2 text-sm ${
                  idx === activeIdx
                    ? "bg-primary-50 text-primary-900"
                    : "text-neutral-800 hover:bg-neutral-50"
                }`}
                data-testid="task-ticket-picker-option"
              >
                <div className="font-mono text-xs font-medium">
                  {row.public_code}
                </div>
                <div className="truncate text-xs text-neutral-500">
                  {row.subject}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default TaskTicketPicker;
